import React, { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import { useNavigate } from "react-router-dom";
import { getJobs } from "../utils/jobUtils";
import { getEquipment } from "../utils/equipmentUtils";
import { getActions } from "../utils/actionUtils";
import { getContacts } from "../utils/contactUtils";
import { getFunctions, httpsCallable } from "firebase/functions";
import CustomButton from "../components/common/CustomButton";
import CustomInput from "../components/common/CustomInput";
import { COLORS } from "../constants/colors.js";
import { FONTS } from "../constants/fonts.js";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import moment from "moment";
import {
  UserIcon,
  DocumentTextIcon,
  CalendarIcon,
  NumberedListIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";

const styles = {
  safeArea: {
    flex: 1,
    backgroundColor: COLORS.white,
  },
  container: {
    flex: 1,
    backgroundColor: COLORS.white,
    minHeight: "100vh",
    padding: "16px 24px",
    paddingTop: 16,
    maxWidth: 1400,
    margin: "0 auto",
  },
  headerContainer: {
    display: "flex",
    justifyContent: "flex-start",
    alignItems: "center",
    marginBottom: 12,
    animation: "fadeIn 0.5s ease-in-out",
  },
  title: {
    fontSize: 32,
    fontFamily: FONTS.bold,
    color: COLORS.text,
    fontWeight: 700,
  },
  content: {
    maxWidth: 500,
    margin: "0 auto",
  },
  toggleContainer: {
    display: "flex",
    justifyContent: "space-between",
    marginBottom: 16,
  },
  toggleButton: {
    flex: 1,
    padding: 12,
    borderRadius: 8,
    borderWidth: 1,
    borderStyle: "solid",
    borderColor: COLORS.primary,
    textAlign: "center",
    margin: "0 4px",
    cursor: "pointer",
    transition: "all 0.3s ease",
  },
  selectedToggle: {
    backgroundColor: COLORS.primary,
    color: COLORS.white,
  },
  toggleText: {
    fontSize: 16,
    fontFamily: FONTS.bold,
    color: COLORS.text,
  },
  selectedToggleText: {
    color: COLORS.white,
  },
  select: {
    width: "100%",
    height: 56,
    padding: "0 12px",
    borderRadius: 12,
    border: `1px solid ${COLORS.tertiary}`,
    fontSize: 16,
    fontFamily: FONTS.regularInter,
    backgroundColor: `${COLORS.secondary}f0`,
    backdropFilter: "blur(10px)",
    WebkitBackdropFilter: "blur(10px)",
    boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
    transition: "all 0.3s ease",
    marginBottom: 12,
    "&:hover": {
      borderColor: COLORS.primary,
      boxShadow: `0 4px 12px rgba(${parseInt(
        COLORS.primary.slice(1, 3),
        16
      )}, ${parseInt(COLORS.primary.slice(3, 5), 16)}, ${parseInt(
        COLORS.primary.slice(5, 7),
        16
      )}, 0.2)`,
    },
  },
  datePickerContainer: {
    display: "flex",
    alignItems: "center",
    width: "100%",
    height: 56,
    borderRadius: 12,
    border: `1px solid ${COLORS.tertiary}`,
    backgroundColor: COLORS.white,
    boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
    transition: "all 0.3s ease",
    marginBottom: 12,
    "&:hover": {
      borderColor: COLORS.primary,
    },
  },
  datePicker: {
    flex: 1,
    height: 46,
    padding: 12,
    border: "none",
    fontSize: 16,
    fontFamily: FONTS.regularInter,
    backgroundColor: "transparent",
    outline: "none",
  },
  datePickerText: {
    fontSize: 16,
    color: COLORS.text,
    fontFamily: FONTS.regularInter,
    padding: "0 12px",
    width: 120,
  },
  descriptionInput: {
    height: 120,
    marginBottom: 12,
  },
  saveButton: {
    marginTop: 12,
    marginBottom: 32,
    borderRadius: 12,
  },
  alert: {
    padding: 12,
    backgroundColor: COLORS.error,
    color: COLORS.white,
    borderRadius: 12,
    marginBottom: 12,
    fontFamily: FONTS.regularInter,
    fontSize: 16,
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  alertCloseButton: {
    cursor: "pointer",
  },
  "@keyframes fadeIn": {
    "0%": { opacity: 0, transform: "translateY(10px)" },
    "100%": { opacity: 1, transform: "translateY(0)" },
  },
  "@media (max-width: 768px)": {
    container: {
      padding: "12px 16px",
      paddingTop: 16,
    },
    title: {
      fontSize: 28,
    },
    content: {
      maxWidth: 400,
    },
    select: {
      height: 48,
    },
    datePickerContainer: {
      height: 48,
    },
    datePicker: {
      padding: 10,
      fontSize: 14,
    },
    datePickerText: {
      fontSize: 14,
      padding: "0 10px",
    },
    toggleButton: {
      padding: 10,
    },
    toggleText: {
      fontSize: 14,
    },
  },
};

const CreateManualInvoiceScreen = () => {
  const { user, userType } = useAuth();
  const navigate = useNavigate();
  const [contactName, setContactName] = useState("");
  const [jobId, setJobId] = useState("");
  const [equipmentId, setEquipmentId] = useState("");
  const [description, setDescription] = useState("");
  const [totalPrice, setTotalPrice] = useState("");
  const [invoiceDate, setInvoiceDate] = useState(new Date());
  const [startDate, setStartDate] = useState(new Date());
  const [endDate, setEndDate] = useState(new Date());
  const [notes, setNotes] = useState("");
  const [actionId, setActionId] = useState("");
  const [quantity, setQuantity] = useState("");
  const [unit, setUnit] = useState("");
  const [contactList, setContactList] = useState([]);
  const [jobList, setJobList] = useState([]);
  const [equipmentList, setEquipmentList] = useState([]);
  const [actionList, setActionList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [alertMessage, setAlertMessage] = useState(null);
  const [selectedMode, setSelectedMode] = useState("equipment");
  const functions = getFunctions();
  const generateInvoicePDF = httpsCallable(functions, "generateInvoicePDF");
  const generateInvoiceNumber = httpsCallable(
    functions,
    "generateInvoiceNumber"
  );

  useEffect(() => {
    console.log(
      "CreateManualInvoiceScreen mounted, userType:",
      userType,
      "user:",
      user
    );
  }, [userType, user]);

  useEffect(() => {
    if (!userType) {
      console.log("No userType, redirecting to /create-order");
      setAlertMessage("User type not loaded. Please log in again.");
      navigate("/create-order");
    } else if (userType !== "equipmentMan" && userType !== "both") {
      console.log("Invalid userType, redirecting:", userType);
      setAlertMessage("Access restricted to equipment men.");
      navigate("/create-order");
    }
  }, [userType, navigate]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [contacts, jobs, equipment, actions] = await Promise.all([
          getContacts(),
          getJobs(),
          getEquipment(user?.uid || ""),
          getActions(),
        ]);
        const cleanContacts = contacts
          .map((contact) => ({
            id: contact.id,
            name: contact.name || "Unnamed Contact",
            position: contact.position || "Unknown Position",
          }))
          .sort((a, b) => (a.name || "").localeCompare(b.name || ""));
        const cleanJobs = jobs
          .map((job) => ({
            id: job.id,
            jobName: job.name || "Unnamed Job",
            jobNumber: job.jobNumber || "Unknown Number",
            roles: job.roles || {},
            address: job.address,
          }))
          .sort((a, b) => (a.jobName || "").localeCompare(b.jobName || ""));
        const cleanEquipment = equipment
          .map((equip) => ({
            id: equip.id,
            name: equip.name || "Unnamed Equipment",
          }))
          .sort((a, b) => (a.name || "").localeCompare(b.name || ""));
        const cleanActions = actions
          .map((action) => ({
            id: action.id,
            name: action.name || "Unnamed Action",
            prices: action.prices || {},
          }))
          .sort((a, b) => (a.name || "").localeCompare(b.name || ""));
        console.log(
          "Contacts fetched:",
          JSON.stringify(cleanContacts, null, 2)
        );
        console.log("Jobs fetched:", JSON.stringify(cleanJobs, null, 2));
        console.log(
          "Equipment fetched:",
          JSON.stringify(cleanEquipment, null, 2)
        );
        console.log("Actions fetched:", JSON.stringify(cleanActions, null, 2));
        setContactList(cleanContacts);
        setJobList(cleanJobs.filter((job) => job.roles?.isEquipmentMan));
        setEquipmentList(cleanEquipment);
        setActionList(cleanActions);
      } catch (error) {
        console.error("Failed to fetch data:", error);
        setAlertMessage(
          "Failed to fetch contacts, jobs, equipment, or actions"
        );
      }
    };
    if (user?.uid) {
      fetchData();
    } else {
      console.log("No user.uid, skipping fetch");
    }
  }, [user?.uid]);

  useEffect(() => {
    console.log(
      "invoiceDate:",
      invoiceDate,
      "Formatted:",
      moment(invoiceDate).format("MM/DD/YYYY HH:mm")
    );
    console.log(
      "startDate:",
      startDate,
      "Formatted:",
      moment(startDate).format("MM/DD/YYYY HH:mm")
    );
    console.log(
      "endDate:",
      endDate,
      "Formatted:",
      moment(endDate).format("MM/DD/YYYY HH:mm")
    );
  }, [invoiceDate, startDate, endDate]);

  const handleSaveInvoice = async () => {
    if (
      !contactName ||
      !jobId ||
      !description ||
      !totalPrice ||
      !invoiceDate ||
      !startDate ||
      !endDate
    ) {
      setAlertMessage(
        "Please fill in all required fields: Contact, Job, Description, Total Price, Invoice Date, Start Date, End Date"
      );
      return;
    }

    if (selectedMode === "equipment" && !equipmentId) {
      setAlertMessage("Please select a valid equipment");
      return;
    }

    if (selectedMode === "action" && (!actionId || !unit || !quantity)) {
      setAlertMessage("Please select a valid action, unit, and quantity");
      return;
    }

    const priceValue = parseFloat(totalPrice);
    if (isNaN(priceValue) || priceValue <= 0) {
      setAlertMessage("Please enter a valid positive price");
      return;
    }

    if (quantity && selectedMode === "action") {
      const quantityValue = parseFloat(quantity);
      if (isNaN(quantityValue) || quantityValue <= 0) {
        setAlertMessage("Please enter a valid positive quantity");
        return;
      }
    }

    const selectedStartDate = moment(startDate).startOf("day");
    const selectedEndDate = moment(endDate).startOf("day");

    if (selectedEndDate.isBefore(selectedStartDate)) {
      setAlertMessage("End date cannot be before start date");
      return;
    }

    setLoading(true);

    try {
      const job = jobList.find((j) => j.id === jobId);
      const equip =
        selectedMode === "equipment"
          ? equipmentList.find((e) => e.id === equipmentId)
          : null;
      const action =
        selectedMode === "action" && actionId
          ? actionList.find((a) => a.id === actionId)
          : null;

      if (!job || (selectedMode === "equipment" && !equip)) {
        setAlertMessage(
          `Please select a valid job${
            selectedMode === "equipment" ? ", equipment" : ""
          }`
        );
        console.error(
          "Validation failed:",
          { job: !!job, equip: !!equip, action: !!action },
          { jobId, equipmentId, actionId }
        );
        setLoading(false);
        return;
      }

      const invoiceNumberResponse = await generateInvoiceNumber();
      const invoiceNumber = invoiceNumberResponse.data.invoiceNumber;

      const invoiceData = {
        userId: user.uid,
        type: "manual",
        invoiceNumber,
        contactName,
        jobId,
        jobName: job.jobName || "Unknown Job",
        jobNumber: job.jobNumber || "Unknown Number",
        equipmentId: selectedMode === "equipment" ? equipmentId : null,
        equipmentName: equip ? equip.name : "",
        actionName: action ? action.name : "",
        description,
        totalPrice: priceValue,
        invoiceDate: moment(invoiceDate).toISOString(),
        startDate: moment(startDate).toISOString(),
        endDate: moment(endDate).toISOString(),
        quantity:
          selectedMode === "action" && quantity ? parseFloat(quantity) : null,
        unit: selectedMode === "action" ? unit : "",
        notes: notes || "",
        jobAddress: job.address || "",
      };

      console.log(
        "Sending invoice data:",
        JSON.stringify(invoiceData, null, 2)
      );

      const response = await generateInvoicePDF(invoiceData);
      const { pdfBase64, invoiceNumber: returnedInvoiceNumber } = response.data;

      if (pdfBase64) {
        const byteCharacters = atob(pdfBase64);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        const blob = new Blob([byteArray], { type: "application/pdf" });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = `invoice_${returnedInvoiceNumber}.pdf`;
        link.click();
        URL.revokeObjectURL(url);
        setAlertMessage(
          `Invoice ${returnedInvoiceNumber} generated successfully`
        );
        setTimeout(() => navigate("/create-order"), 3000);
      } else {
        setAlertMessage("Invoice generated but PDF content missing.");
      }
    } catch (error) {
      console.error("Failed to generate invoice:", error);
      setAlertMessage(`Error creating invoice: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const renderContactPicker = () => (
    <select
      value={contactName}
      onChange={(e) => setContactName(e.target.value)}
      style={styles.select}
    >
      <option value="">Select Contact</option>
      {contactList.map((contact) => (
        <option
          key={contact.id}
          value={`${contact.name} - ${contact.position}`}
        >
          {contact.name && contact.position
            ? `${contact.name} - ${contact.position}`
            : "Unnamed Contact"}
        </option>
      ))}
    </select>
  );

  const renderJobPicker = () => (
    <select
      value={jobId}
      onChange={(e) => setJobId(e.target.value)}
      style={styles.select}
    >
      <option value="">Select Job</option>
      {jobList.map((job) => (
        <option key={job.id} value={job.id}>
          {job.jobNumber && job.jobName
            ? `${job.jobNumber} - ${job.jobName}`
            : "Unnamed Job"}
        </option>
      ))}
    </select>
  );

  const renderEquipmentPicker = () => (
    <select
      value={equipmentId}
      onChange={(e) => setEquipmentId(e.target.value)}
      style={styles.select}
    >
      <option value="">Select Equipment</option>
      {equipmentList.map((equipment) => (
        <option key={equipment.id} value={equipment.id}>
          {equipment.name || "Unnamed Equipment"}
        </option>
      ))}
    </select>
  );

  const renderActionPicker = () => (
    <select
      value={actionId}
      onChange={(e) => {
        setActionId(e.target.value);
        setUnit("");
        setQuantity("");
      }}
      style={styles.select}
    >
      <option value="">Select Action</option>
      {actionList.map((action) => (
        <option key={action.id} value={action.id}>
          {action.name || "Unnamed Action"}
        </option>
      ))}
    </select>
  );

  const renderUnitPicker = () => {
    const unitOptions = actionId
      ? Object.keys(
          actionList.find((a) => a.id === actionId)?.prices || {}
        ).map((unit) => ({ id: unit, name: unit }))
      : [];
    return (
      <select
        value={unit}
        onChange={(e) => {
          setUnit(e.target.value);
          setQuantity("");
        }}
        style={styles.select}
      >
        <option value="">Select Unit</option>
        {unitOptions.map((u) => (
          <option key={u.id} value={u.id}>
            {u.name.charAt(0).toUpperCase() + u.name.slice(1)}
          </option>
        ))}
      </select>
    );
  };

  const CustomDateInput = ({ value, onClick, placeholder }) => (
    <div style={styles.datePickerContainer}>
      <CalendarIcon width={24} height={24} stroke={COLORS.primary} />
      <input
        type="text"
        value={value}
        onClick={onClick}
        readOnly
        style={styles.datePicker}
        placeholder={placeholder}
      />
    </div>
  );

  return (
    <div style={styles.safeArea}>
      <div style={styles.container}>
        {alertMessage && (
          <div style={styles.alert}>
            <span>{alertMessage}</span>
            <button
              style={styles.alertCloseButton}
              onClick={() => setAlertMessage(null)}
            >
              <XMarkIcon width={20} height={20} stroke={COLORS.white} />
            </button>
          </div>
        )}
        <div style={styles.headerContainer}>
          <h1 style={styles.title}>Create Manual Invoice</h1>
        </div>
        <div style={styles.content}>
          <div style={styles.toggleContainer}>
            <button
              style={{
                ...styles.toggleButton,
                ...(selectedMode === "equipment" ? styles.selectedToggle : {}),
              }}
              onClick={() => {
                setSelectedMode("equipment");
                setActionId("");
                setUnit("");
                setQuantity("");
              }}
            >
              <span
                style={{
                  ...styles.toggleText,
                  ...(selectedMode === "equipment"
                    ? styles.selectedToggleText
                    : {}),
                }}
              >
                Equipment
              </span>
            </button>
            <button
              style={{
                ...styles.toggleButton,
                ...(selectedMode === "action" ? styles.selectedToggle : {}),
              }}
              onClick={() => {
                setSelectedMode("action");
                setEquipmentId("");
              }}
            >
              <span
                style={{
                  ...styles.toggleText,
                  ...(selectedMode === "action"
                    ? styles.selectedToggleText
                    : {}),
                }}
              >
                Action
              </span>
            </button>
          </div>
          {renderContactPicker()}
          {renderJobPicker()}
          {selectedMode === "equipment" && renderEquipmentPicker()}
          {selectedMode === "action" && (
            <>
              {renderActionPicker()}
              {renderUnitPicker()}
              <CustomInput
                value={quantity}
                onChangeText={(quantity) => {
                  setQuantity(quantity);
                }}
                icon={
                  <NumberedListIcon
                    width={24}
                    height={24}
                    stroke={COLORS.primary}
                  />
                }
                placeholder="Enter quantity"
                type="number"
                style={styles.descriptionInput}
              />
            </>
          )}
          <CustomInput
            value={description}
            onChangeText={setDescription}
            icon={
              <DocumentTextIcon
                width={24}
                height={24}
                stroke={COLORS.primary}
              />
            }
            placeholder="Enter invoice description"
            multiline
            style={styles.descriptionInput}
          />
          <CustomInput
            value={totalPrice}
            onChangeText={setTotalPrice}
            icon={
              <NumberedListIcon
                width={24}
                height={24}
                stroke={COLORS.primary}
              />
            }
            placeholder="Enter total price"
            type="number"
            style={styles.descriptionInput}
          />
          <div style={styles.datePickerContainer}>
            <span style={styles.datePickerText}>Invoice Date</span>
            <DatePicker
              selected={invoiceDate}
              onChange={(date) => setInvoiceDate(date)}
              showTimeSelect
              timeFormat="HH:mm"
              timeIntervals={15}
              dateFormat="MM/dd/yyyy HH:mm"
              placeholderText="Select invoice date"
              customInput={
                <CustomDateInput placeholder="Select invoice date" />
              }
              withPortal={false}
            />
          </div>
          <div style={styles.datePickerContainer}>
            <span style={styles.datePickerText}>Start Date</span>
            <DatePicker
              selected={startDate}
              onChange={(date) => setStartDate(date)}
              showTimeSelect
              timeFormat="HH:mm"
              timeIntervals={15}
              dateFormat="MM/dd/yyyy HH:mm"
              placeholderText="Select start date"
              customInput={<CustomDateInput placeholder="Select start date" />}
              withPortal={false}
            />
          </div>
          <div style={styles.datePickerContainer}>
            <span style={styles.datePickerText}>End Date</span>
            <DatePicker
              selected={endDate}
              onChange={(date) => setEndDate(date)}
              showTimeSelect
              timeFormat="HH:mm"
              timeIntervals={15}
              dateFormat="MM/dd/yyyy HH:mm"
              placeholderText="Select end date"
              customInput={<CustomDateInput placeholder="Select end date" />}
              withPortal={false}
            />
          </div>
          <CustomInput
            value={notes}
            onChangeText={setNotes}
            icon={
              <DocumentTextIcon
                width={24}
                height={24}
                stroke={COLORS.primary}
              />
            }
            placeholder="Enter any additional notes (optional)"
            multiline
            style={styles.descriptionInput}
          />
          <CustomButton
            title={loading ? "Saving..." : "Generate Invoice"}
            onPress={handleSaveInvoice}
            style={styles.saveButton}
            disabled={loading}
          />
        </div>
      </div>
    </div>
  );
};

export default CreateManualInvoiceScreen;
